import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const GENDERS = [
  { id: "male", label: "Men" },
  { id: "female", label: "Women" },
  { id: "non_binary", label: "Non-binary" },
  { id: "other", label: "Other" },
];

const PreferencesStep = ({ onNext }) => {
  const [ageMin, setAgeMin] = useState(25);
  const [ageMax, setAgeMax] = useState(45);
  const [maxDistance, setMaxDistance] = useState(50);
  const [seekingGender, setSeekingGender] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleGender = (genderId) => {
    setSeekingGender(prev =>
      prev.includes(genderId)
        ? prev.filter(g => g !== genderId)
        : [...prev, genderId]
    );
  };

  const handleSubmit = async () => {
    if (seekingGender.length === 0) {
      toast.error("Please select who you'd like to meet");
      return;
    }
    if (ageMin >= ageMax) {
      toast.error("Maximum age must be greater than minimum age");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitPreferences({
        age_min: ageMin,
        age_max: ageMax,
        max_distance: maxDistance,
        seeking_gender: seekingGender
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Match Preferences</h2>
      <p className="text-gray-600 mb-6">Who would you like to meet?</p>

      {/* Seeking Gender */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          I&apos;m interested in meeting...
        </label>
        <div className="flex flex-wrap gap-2">
          {GENDERS.map(gender => (
            <button
              key={gender.id}
              onClick={() => toggleGender(gender.id)}
              className={`px-4 py-2.5 rounded-full border-2 font-medium transition-all ${
                seekingGender.includes(gender.id)
                  ? 'border-[#1C3A7A] bg-[#1C3A7A] text-white'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {gender.label}
            </button>
          ))}
        </div>
      </div>

      {/* Age Range */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Age range: {ageMin} - {ageMax}
        </label>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Min age</label>
            <input
              type="range"
              min={18}
              max={80}
              value={ageMin}
              onChange={(e) => setAgeMin(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1C3A7A]"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Max age</label>
            <input
              type="range"
              min={18}
              max={80}
              value={ageMax}
              onChange={(e) => setAgeMax(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1C3A7A]"
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>18</span>
          <span>80</span>
        </div>
      </div>

      {/* Distance */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Maximum distance: {maxDistance} miles
        </label>
        <input
          type="range"
          min={5}
          max={500}
          step={5}
          value={maxDistance}
          onChange={(e) => setMaxDistance(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1C3A7A]"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>5 mi</span>
          <span>500 mi</span>
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || seekingGender.length === 0}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default PreferencesStep;
